import React, { useState, useEffect, useRef } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import FaceDetection from '../components/FaceDetection'
import Timer from '../components/Timer'
import LoadingScreen from '../components/LoadingScreen'
import alert from '../utils/alertService'
import './TestTaking.css'

const TestTaking = () => {
  const { testId } = useParams()
  const history = useHistory()
  const videoRef = useRef(null)
  const faceDetectionRef = useRef(null)
  const [test, setTest] = useState(null)
  const [questions, setQuestions] = useState([])
  const [answers, setAnswers] = useState({})
  const [attemptId, setAttemptId] = useState(null)
  const [timeRemaining, setTimeRemaining] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [faceDetectionEnabled, setFaceDetectionEnabled] = useState(false)
  const [violations, setViolations] = useState([])
  const [tabSwitchCount, setTabSwitchCount] = useState(0)
  const [showWarningModal, setShowWarningModal] = useState(false)
  const [warningMessage, setWarningMessage] = useState('')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showFullscreenBlock, setShowFullscreenBlock] = useState(false)
  const [autoSubmitMessage, setAutoSubmitMessage] = useState('')
  const [maxViolations, setMaxViolations] = useState(3)
  const violationTimeoutRef = useRef(null)
  const lastViolationTimeRef = useRef(0)
  const violationCountRef = useRef(0)
  const autoSubmittedRef = useRef(false)
  const cleanupDoneRef = useRef(false)
  const testStartTimeRef = useRef(Date.now()) // Track when test UI loaded

  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (!token) {
      history.push('/login')
      return
    }

    startTest()
  }, [testId, history])

  const startTest = async () => {
    // Reset state at the start to ensure clean slate when navigating between tests
    setLoading(true)
    setFaceDetectionEnabled(false)
    setError('')

    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(`/api/tests/${testId}/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setTest(data.test)
        setQuestions(data.questions)
        setAttemptId(data.attempt_id)
        
        // Use remaining_seconds if resuming, otherwise use full duration
        const remainingTime = data.remaining_seconds !== undefined 
          ? data.remaining_seconds 
          : data.duration_minutes * 60
        
        setTimeRemaining(remainingTime)
        setFaceDetectionEnabled(true)
        
        // Set max violations from test settings
        if (data.max_violations) {
          setMaxViolations(data.max_violations)
        }
        
        // Restore saved answers from localStorage if resuming
        if (data.resumed) {
          const savedAnswers = localStorage.getItem(`test_answers_${data.attempt_id}`)
          if (savedAnswers) {
            try {
              setAnswers(JSON.parse(savedAnswers))
            } catch (err) {
              console.error('Failed to restore answers:', err)
            }
          }
          
          // Restore violation count from backend (don't reset!)
          if (data.violation_count !== undefined) {
            violationCountRef.current = data.violation_count
            setTabSwitchCount(data.violation_count)
          }
          
          // Log refresh violation (increment from restored count)
          violationCountRef.current += 1
          setTabSwitchCount(violationCountRef.current)
          
          // Show warning (use max_violations from response, fallback to state)
          const testMaxViolations = data.max_violations || maxViolations
          setWarningMessage(`Warning ${violationCountRef.current}/${testMaxViolations}: Page refresh detected`)
          setShowWarningModal(true)
          
          // Auto-close warning after 3 seconds
          if (violationTimeoutRef.current) {
            clearTimeout(violationTimeoutRef.current)
          }
          violationTimeoutRef.current = setTimeout(() => {
            setShowWarningModal(false)
          }, 3000)
          
          // Log violation to backend
          try {
            const violationResponse = await fetch('/api/tests/log-violation', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                attempt_id: data.attempt_id,
                type: 'page_refresh',
                severity: 'high',
                details: 'Student refreshed the page during test'
              })
            })
            
            if (violationResponse.ok) {
              const violationData = await violationResponse.json()
              const testMaxViolations = data.max_violations || maxViolations
              if (violationData.auto_submit || violationCountRef.current >= testMaxViolations) {
                if (!autoSubmittedRef.current) {
                  autoSubmittedRef.current = true
                  setAutoSubmitMessage('Test has been auto-submitted due to multiple violations.')
                  setTimeout(() => {
                    handleSubmitTest(true) // Pass true to indicate auto-submit
                  }, 1500)
                }
              }
            }
          } catch (err) {
            console.error('Failed to log refresh violation:', err)
          }
        } else {
          // Reset violation tracking for new test
          violationCountRef.current = 0
          setTabSwitchCount(0)
          autoSubmittedRef.current = false
          setAutoSubmitMessage('')
        }

        // Always reset cleanup flag when starting a test (whether new or resumed)
        cleanupDoneRef.current = false
        
        // Check if time has expired
        if (remainingTime <= 0) {
          alert.warning('Test time has expired. Submitting now...', 0)
          setTimeout(() => {
            handleSubmitTest(true) // Pass true to indicate auto-submit
          }, 1500)
        }
      } else {
        const errorData = await response.json()
        
        // Handle schedule-related errors
        if (errorData.schedule_status === 'not_started') {
          const timeRemaining = formatTimeRemaining(errorData.time_until_start_seconds)
          setError(`Test has not started yet. It will be available in ${timeRemaining}.`)
        } else if (errorData.schedule_status === 'ended') {
          setError('Test time has passed. This test is no longer available.')
        } else {
          setError(errorData.error || 'Failed to start test')
        }
      }
    } catch (err) {
      setError('Network error')
    } finally {
      setLoading(false)
    }
  }

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => {
      const newAnswers = {
        ...prev,
        [questionId]: answer
      }
      // Save answers to localStorage for persistence
      if (attemptId) {
        localStorage.setItem(`test_answers_${attemptId}`, JSON.stringify(newAnswers))
      }
      return newAnswers
    })
  }

  // Helper function to stop the camera with full cleanup
  const stopCamera = (shouldCleanupBackend = true) => {
    // Only prevent duplicate backend cleanups, but always try to stop camera
    const shouldCallBackend = shouldCleanupBackend && !cleanupDoneRef.current && attemptId

    // Mark as cleanup done to prevent duplicate backend calls
    if (shouldCleanupBackend) {
      cleanupDoneRef.current = true
    }

    // First disable face detection to trigger cleanup via useEffect
    setFaceDetectionEnabled(false)

    // Also call the exposed stopCamera method if available (always try this)
    if (faceDetectionRef.current && faceDetectionRef.current.stopCamera) {
      try {
        faceDetectionRef.current.stopCamera()
      } catch (e) {
        console.warn('Camera stop error (may already be stopped):', e)
      }
    }

    // Call backend cleanup to release pattern analyzer session
    if (shouldCallBackend) {
      const token = localStorage.getItem('access_token')
      fetch(`/api/face/cleanup-session/${attemptId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      }).catch(() => {}) // Ignore errors on cleanup - it's best-effort
    }
  }

  const handleSubmitTest = async (isAutoSubmit = false) => {
    // Allow auto-submit to proceed, but prevent duplicate submissions
    if (isSubmitting) return
    if (!isAutoSubmit && autoSubmittedRef.current) return

    setIsSubmitting(true)

    // Stop the camera immediately when submitting - use async method if available
    if (faceDetectionRef.current?.stopCameraAsync) {
      await faceDetectionRef.current.stopCameraAsync()
    } else {
      stopCamera()
    }

    // Clear auto-submit message when starting submission
    if (isAutoSubmit) {
      setAutoSubmitMessage('')
    }
    
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(`/api/tests/${testId}/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ answers })
      })

      if (response.ok) {
        const data = await response.json()
        
        // Clean up localStorage
        if (attemptId) {
          localStorage.removeItem(`test_answers_${attemptId}`)
        }
        
        // Clear auto-submit message before navigating
        setAutoSubmitMessage('')
        
        // Navigate to result page
        history.push('/test-result', { 
          testData: test, 
          result: data,
          attemptId: attemptId 
        })
      } else {
        const errorData = await response.json()
        setError(errorData.error || 'Failed to submit test')
        // Clear auto-submit message on error too
        setAutoSubmitMessage('')
      }
    } catch (err) {
      setError('Network error')
      // Clear auto-submit message on error
      setAutoSubmitMessage('')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleTimeUp = () => {
    handleSubmitTest()
  }

  const formatTimeRemaining = (seconds) => {
    if (!seconds || seconds <= 0) return 'now'
    
    const days = Math.floor(seconds / (24 * 60 * 60))
    const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60))
    const minutes = Math.floor((seconds % (60 * 60)) / 60)
    
    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours > 1 ? 's' : ''}`
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`
    } else {
      return `${minutes} minute${minutes > 1 ? 's' : ''}`
    }
  }

  const handleFaceViolation = (violation) => {
    setViolations(prev => [...prev, violation])

    // Sync violation count if provided in violation object
    if (violation.total_violations !== undefined) {
      violationCountRef.current = violation.total_violations
      setTabSwitchCount(violation.total_violations)
    }

    // Auto-submit if too many violations
    if (violations.length >= maxViolations) {
      handleSubmitTest()
    }
  }

  // Handle total violations update from FaceDetection (syncs on every frame)
  const handleTotalViolationsChange = (total) => {
    violationCountRef.current = total
    setTabSwitchCount(total)
  }

  const handleFullscreenRequest = () => {
    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen()
    }
  }

  // Tab switch and visibility detection
  useEffect(() => {
    if (!attemptId) return

    let visibilityCheckInterval = null

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Skip during initial 5-second grace period after test load
        if (Date.now() - testStartTimeRef.current < 5000) {
          return
        }
        // Debounce: only log if last violation was more than 2 seconds ago
        const now = Date.now()
        if (now - lastViolationTimeRef.current > 2000) {
          lastViolationTimeRef.current = now
          logViolation('tab_switch', 'high', 'Student switched tabs or minimized window')
        }
      }
    }

    const handleBlur = () => {
      // Skip blur events during initial 5-second grace period after test load
      if (Date.now() - testStartTimeRef.current < 5000) {
        return
      }
      // Debounce: only log if last violation was more than 2 seconds ago
      const now = Date.now()
      if (now - lastViolationTimeRef.current > 2000) {
        lastViolationTimeRef.current = now
        logViolation('focus_loss', 'medium', 'Student clicked outside the test window')
      }
    }

    // Detect when window regains focus (to catch desktop switching)
    const handleFocus = () => {
      // Skip during initial 5-second grace period
      if (Date.now() - testStartTimeRef.current < 5000) {
        return
      }
      // Check visibility state after a short delay
      setTimeout(() => {
        // If we just regained focus but were hidden, it might be a desktop switch
        if (!document.hidden) {
          // Check if we actually have focus
          if (document.hasFocus && !document.hasFocus()) {
            const now = Date.now()
            if (now - lastViolationTimeRef.current > 2000) {
              lastViolationTimeRef.current = now
              logViolation('tab_switch', 'high', 'Student switched windows or desktops')
            }
          }
        }
      }, 100)
    }

    // Periodic check for visibility (catches desktop switching better)
    const checkVisibility = () => {
      // Skip during initial 5-second grace period
      if (Date.now() - testStartTimeRef.current < 5000) {
        return
      }
      if (document.hidden) {
        const now = Date.now()
        if (now - lastViolationTimeRef.current > 2000) {
          lastViolationTimeRef.current = now
          logViolation('tab_switch', 'high', 'Student switched tabs, windows, or desktops')
        }
      }
    }

    // Set up event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('blur', handleBlur)
    window.addEventListener('focus', handleFocus)
    
    // Periodic check every 500ms to catch desktop switches
    visibilityCheckInterval = setInterval(checkVisibility, 500)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('blur', handleBlur)
      window.removeEventListener('focus', handleFocus)
      if (visibilityCheckInterval) {
        clearInterval(visibilityCheckInterval)
      }
    }
  }, [attemptId])

  // Fullscreen enforcement
  useEffect(() => {
    if (!test) return

    // Request fullscreen on test start
    const enterFullscreen = () => {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch(err => {
          console.log('Fullscreen request failed:', err)
        })
      }
    }

    // Enter fullscreen after a short delay
    const timer = setTimeout(enterFullscreen, 1000)

    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!document.fullscreenElement
      setIsFullscreen(isCurrentlyFullscreen)

      if (!isCurrentlyFullscreen && !isSubmitting) {
        // User exited fullscreen - just show the re-enter prompt
        // Don't log as violation (ESC key can accidentally trigger this)
        setShowFullscreenBlock(true)
      } else {
        setShowFullscreenBlock(false)
      }
    }

    // Also listen for other fullscreen events (for browser compatibility)
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    document.addEventListener('mozfullscreenchange', handleFullscreenChange)
    document.addEventListener('MSFullscreenChange', handleFullscreenChange)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange)
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange)
      // Exit fullscreen when component unmounts
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {})
      }
    }
  }, [test, isSubmitting, attemptId])

  // Enhanced copy-paste and keyboard prevention + Hard refresh prevention
  useEffect(() => {
    if (!attemptId) return

    const handleContextMenu = (e) => e.preventDefault()
    
    const handleKeyDown = (e) => {
      // Prevent ESC key from exiting fullscreen (ESC triggers fullscreen exit which is logged as violation)
      if (e.key === 'Escape' || e.keyCode === 27) {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        return false
      }

      // Prevent hard refresh: Ctrl+R, Ctrl+Shift+R, F5, Ctrl+F5
      const isRefreshKey = e.key === 'F5' ||
                           e.key === 'r' ||
                           e.key === 'R' ||
                           (e.keyCode === 116) // F5 keyCode

      const isRefreshCombo = (e.ctrlKey && (e.key === 'r' || e.key === 'R')) ||
                              (e.ctrlKey && e.shiftKey && (e.key === 'r' || e.key === 'R')) ||
                              (e.ctrlKey && e.key === 'F5')

      if (isRefreshKey || isRefreshCombo) {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        
        // Log violation attempt
        if (attemptId) {
          const token = localStorage.getItem('access_token')
          fetch('/api/tests/log-violation', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              attempt_id: attemptId,
              type: 'hard_refresh_attempt',
              severity: 'high',
              details: 'Student attempted to hard refresh the page'
            })
          }).catch(err => console.error('Failed to log refresh attempt:', err))
          
          violationCountRef.current += 1
          setTabSwitchCount(violationCountRef.current)
        }
        
        alert.warning('Hard refresh is disabled during the test. This action has been logged as a violation.', 5000)
        return false
      }
      
      // Prevent copy, paste, cut
      if (e.ctrlKey && (e.key === 'c' || e.key === 'v' || e.key === 'x' || e.key === 'a')) {
        e.preventDefault()
      }
      // Prevent F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U
      if (e.key === 'F12' || 
          (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) ||
          (e.ctrlKey && e.key === 'U')) {
        e.preventDefault()
      }
      // Prevent print
      if (e.ctrlKey && e.key === 'p') {
        e.preventDefault()
      }
    }

    const handleCopy = (e) => e.preventDefault()
    const handleCut = (e) => e.preventDefault()
    const handlePaste = (e) => e.preventDefault()
    const handleDragStart = (e) => e.preventDefault()

    // Prevent page unload/refresh with warning
    const handleBeforeUnload = (e) => {
      e.preventDefault()
      e.returnValue = 'You are taking a test. Refreshing or leaving this page will be logged as a violation. Are you sure you want to leave?'
      return e.returnValue
    }

    document.addEventListener('contextmenu', handleContextMenu)
    document.addEventListener('keydown', handleKeyDown, true) // Use capture phase
    document.addEventListener('copy', handleCopy)
    document.addEventListener('cut', handleCut)
    document.addEventListener('paste', handlePaste)
    document.addEventListener('dragstart', handleDragStart)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('keydown', handleKeyDown, true)
      document.removeEventListener('copy', handleCopy)
      document.removeEventListener('cut', handleCut)
      document.removeEventListener('paste', handlePaste)
      document.removeEventListener('dragstart', handleDragStart)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [attemptId])

  // Comprehensive camera cleanup on navigation and page unload
  useEffect(() => {
    if (!attemptId) return

    // Cleanup on browser back/forward navigation
    const handlePopState = () => {
      stopCamera(true)
    }

    // Cleanup on page unload (closing tab, navigating away)
    const handleUnload = () => {
      stopCamera(true)
    }

    window.addEventListener('popstate', handlePopState)
    window.addEventListener('unload', handleUnload)

    // Cleanup on component unmount
    return () => {
      window.removeEventListener('popstate', handlePopState)
      window.removeEventListener('unload', handleUnload)
      // Stop camera on unmount if not already done
      if (!cleanupDoneRef.current) {
        stopCamera(true)
      }
    }
  }, [attemptId])

  const logViolation = async (type, severity, message) => {
    if (!attemptId) return

    // Increment violation count using ref for accurate tracking
    violationCountRef.current += 1
    const newViolationCount = violationCountRef.current
    
    // Update state for UI
    setTabSwitchCount(newViolationCount)
    
    // Show warning modal
    setWarningMessage(`Warning ${newViolationCount}/${maxViolations}: ${message}`)
    setShowWarningModal(true)

    // Clear any existing timeout
    if (violationTimeoutRef.current) {
      clearTimeout(violationTimeoutRef.current)
    }

    // Auto-close warning after 3 seconds
    violationTimeoutRef.current = setTimeout(() => {
      setShowWarningModal(false)
    }, 3000)

    // Log violation to backend
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch('/api/tests/log-violation', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          attempt_id: attemptId,
          type: type,
          severity: severity,
          details: message
        })
      })

      if (response.ok) {
        const data = await response.json()

        // Sync violation count from backend to ensure accuracy
        if (data.total_violations !== undefined) {
          violationCountRef.current = data.total_violations
          setTabSwitchCount(data.total_violations)
        }

        // Check if test should be auto-submitted (only once)
        if ((data.auto_submit || violationCountRef.current >= maxViolations) && !autoSubmittedRef.current) {
          autoSubmittedRef.current = true
          setAutoSubmitMessage('Test has been auto-submitted due to multiple violations.')
          // Submit after a short delay to show the message
          setTimeout(() => {
            handleSubmitTest(true) // Pass true to indicate auto-submit
          }, 1500)
        }
      }
    } catch (err) {
      console.error('Failed to log violation:', err)
    }
  }

  const handleReenterFullscreen = () => {
    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen().then(() => {
        setShowFullscreenBlock(false)
      }).catch(err => {
        console.log('Fullscreen request failed:', err)
      })
    }
  }

  if (loading) {
    return <LoadingScreen message="Loading Test" />
  }

  if (error) {
    return (
      <div className="test-container">
        <div className="test-error-container">
          <div className="test-error-icon">⚠️</div>
          <h2>Cannot Start Test</h2>
          <div className="test-error-message">{error}</div>
          <button 
            onClick={() => history.push('/available-tests')}
            className="back-to-tests-button"
          >
            Back to Available Tests
          </button>
          <button 
            onClick={() => history.push('/student-dashboard')}
            className="back-to-dashboard-button"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="test-container">
      {/* Warning Modal */}
      {showWarningModal && (
        <div className="warning-modal">
          <div className="warning-modal-content">
            <h2>⚠️ Violation Detected</h2>
            <p>{warningMessage}</p>
            <p className="warning-consequence">
              {tabSwitchCount >= maxViolations ? 
                'Your test will be auto-submitted!' : 
                `${maxViolations - tabSwitchCount} warning${maxViolations - tabSwitchCount !== 1 ? 's' : ''} remaining before auto-submission`}
            </p>
          </div>
        </div>
      )}

      {/* Fullscreen Block Modal */}
      {showFullscreenBlock && (
        <div className="fullscreen-block-modal">
          <div className="fullscreen-block-content">
            <h2>🔒 Test Paused</h2>
            <p>Please enter fullscreen mode to continue the test</p>
            <button onClick={handleReenterFullscreen} className="reenter-fullscreen-btn">
              Enter Fullscreen Mode
            </button>
            <p className="fullscreen-help">
              This action has been logged as a violation.
            </p>
          </div>
        </div>
      )}

      {/* Auto-Submit Message - Hide when submitting */}
      {autoSubmitMessage && !isSubmitting && (
        <div className="auto-submit-message">
          <div className="auto-submit-content">
            <h2>⚠️ Test Auto-Submitted</h2>
            <p>{autoSubmitMessage}</p>
            <p className="auto-submit-note">Submitting your test now...</p>
          </div>
        </div>
      )}

      <div className="test-header">
        <h1>{test?.title}</h1>
        <div className="test-controls">
          <Timer 
            timeRemaining={timeRemaining} 
            onTimeUp={handleTimeUp}
          />
          <button 
            className="fullscreen-button"
            onClick={handleFullscreenRequest}
          >
            {isFullscreen ? '🔓 Exit Fullscreen' : '🔒 Fullscreen'}
          </button>
        </div>
      </div>

      {tabSwitchCount > 0 && (
        <div className="violations-warning">
          <h3>⚠️ Warning: {tabSwitchCount} violation(s) detected</h3>
          <p>Please ensure you are looking at the screen, no one else is in the frame, and stay on this tab.</p>
        </div>
      )}

      <div className="test-content">
        <div className="questions-section">
          {questions.map((question, index) => (
            <div key={question.id} className="question-card">
              <div className="question-header">
                <h3>Question {index + 1}</h3>
                <span className="points">{question.points} point(s)</span>
              </div>
              
              <p className="question-text">{question.question_text}</p>
              
              <div className="options">
                {question.options.map((option, optionIndex) => (
                  <label key={optionIndex} className="option-label">
                    <input
                      type="radio"
                      name={`question_${question.id}`}
                      value={optionIndex}
                      checked={answers[question.id] === optionIndex}
                      onChange={() => handleAnswerChange(question.id, optionIndex)}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="test-sidebar">
          <div className="face-detection-section">
            <h3>Face Detection</h3>
            <FaceDetection
              ref={faceDetectionRef}
              enabled={faceDetectionEnabled}
              attemptId={attemptId}
              onViolation={handleFaceViolation}
              onTotalViolationsChange={handleTotalViolationsChange}
              onAutoSubmit={() => handleSubmitTest(true)}
              maxViolations={maxViolations}
            />
          </div>

          <div className="progress-section">
            <h3>Progress</h3>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ 
                  width: `${(Object.keys(answers).length / questions.length) * 100}%` 
                }}
              />
            </div>
            <p>{Object.keys(answers).length} / {questions.length} questions answered</p>
          </div>

          <button 
            className="test-submit-button"
            onClick={handleSubmitTest}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Test'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default TestTaking
